import React, { useState, useEffect, useMemo } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  TextField,
  Divider,
  Alert,
  Chip,
} from '@mui/material'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import WarningIcon from '@mui/icons-material/Warning'
import { AutoCoderFramework, ImportResult, VariableNames } from '../../types/gotypes'

interface ImportConfirmationModalProps {
  open: boolean
  importName: string
  variablePath: string
  frameworks: AutoCoderFramework[]
  error?: string | null
  loading?: boolean
  importResult?: ImportResult | null
  onConfirm: (variablePath: string, variableNames: VariableNames) => void
  onCancel: () => void
  onChangeFolder: () => void
  onViewVariables?: () => void
}

// Validate base name: must start with a letter, only alphanumeric + underscore
const isValidBaseName = (name: string): boolean => /^[a-z][a-z0-9_]*$/.test(name)

const ImportConfirmationModal: React.FC<ImportConfirmationModalProps> = ({
  open,
  importName,
  variablePath,
  frameworks,
  error,
  loading,
  importResult,
  onConfirm,
  onCancel,
  onChangeFolder,
  onViewVariables,
}) => {
  const [baseName, setBaseName] = useState(importName.toLowerCase())

  // Reset to defaults when modal opens or importName changes
  useEffect(() => {
    if (open && !importResult) {
      setBaseName(importName.toLowerCase())
    }
  }, [open, importName, importResult])

  // Derive variable names from framework
  const variablePreview = useMemo(() => {
    const framework = frameworks?.[0]
    if (!framework) return []
    const names: { name: string; description: string }[] = []
    names.push({
      name: `${baseName}_${framework.main_variable.name.toLowerCase()}`,
      description: `main variable (${framework.main_variable.name})`,
    })
    for (const looped of framework.looped_variables || []) {
      names.push({
        name: `${baseName}_${looped.name.toLowerCase()}`,
        description: `looped variable (${looped.name})`,
      })
    }
    return names
  }, [baseName, frameworks])

  const handleConfirm = () => {
    const variableNames: VariableNames = {
      base_name: baseName,
    }
    onConfirm(variablePath, variableNames)
  }

  const baseNameValid = isValidBaseName(baseName)
  const hasWarnings = importResult && importResult.warnings && importResult.warnings.length > 0

  // If we have an import result, show the summary view
  if (importResult) {
    return (
      <Dialog open={open} onClose={onCancel} maxWidth="sm" fullWidth>
        <DialogTitle>Import Complete</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <Alert severity={hasWarnings ? 'warning' : 'success'} sx={{ mb: 2 }}>
              {hasWarnings
                ? `Import completed with ${importResult.warnings.length} warning${importResult.warnings.length !== 1 ? 's' : ''}`
                : `Successfully imported ${importResult.variables_created.length} variable${importResult.variables_created.length !== 1 ? 's' : ''}`}
            </Alert>

            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {importResult.cases_processed} / {importResult.total_cases} cases processed
              </Typography>
            </Box>

            {importResult.variable_stats && importResult.variable_stats.length > 0 && (
              <Box
                sx={{
                  p: 2,
                  bgcolor: 'background.default',
                  borderRadius: 1,
                  border: 1,
                  borderColor: 'divider',
                  mb: 2,
                }}
              >
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Variable Details:
                </Typography>
                {importResult.variable_stats.map((vs) => (
                  <Box
                    key={vs.name}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      py: 0.5,
                      '&:not(:last-child)': { borderBottom: 1, borderColor: 'divider' },
                    }}
                  >
                    <Box>
                      <Typography variant="body2" fontWeight="bold">
                        {vs.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {vs.coded_lines} / {vs.total_lines} cases coded
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                      {vs.verified && (
                        <Chip
                          icon={<CheckCircleIcon />}
                          label="Verified"
                          size="small"
                          color="success"
                          variant="outlined"
                        />
                      )}
                      {vs.tabulation_ok && (
                        <Chip
                          icon={<CheckCircleIcon />}
                          label="Tabulation OK"
                          size="small"
                          color="success"
                          variant="outlined"
                        />
                      )}
                      {!vs.has_data && (
                        <Chip
                          icon={<WarningIcon />}
                          label="No Data"
                          size="small"
                          color="warning"
                          variant="outlined"
                        />
                      )}
                      {vs.retries > 0 && (
                        <Chip
                          label={`${vs.retries} retries`}
                          size="small"
                          color="warning"
                          variant="outlined"
                        />
                      )}
                    </Box>
                  </Box>
                ))}
              </Box>
            )}

            {hasWarnings && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Warnings:
                </Typography>
                {importResult.warnings.map((w, i) => (
                  <Alert key={i} severity="warning" sx={{ mb: 0.5 }}>
                    {w}
                  </Alert>
                ))}
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onCancel} variant="outlined">
            Close
          </Button>
          {onViewVariables && (
            <Button onClick={onViewVariables} variant="contained" color="info">
              View Variables
            </Button>
          )}
        </DialogActions>
      </Dialog>
    )
  }

  return (
    <Dialog
      open={open}
      onClose={onCancel}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>Confirm Import</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 1 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Review the import details below and confirm to create the variables in Carbon.
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box
            sx={{
              p: 2,
              mb: 2,
              borderRadius: 1,
              border: 1,
              borderColor: 'info.main',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Destination Folder:
              </Typography>
              <Typography variant="body1" fontWeight="bold" color="info.main">
                {variablePath}
              </Typography>
            </Box>
            <Button
              variant="outlined"
              size="small"
              color="info"
              onClick={onChangeFolder}
              sx={{ ml: 2 }}
            >
              Change Folder
            </Button>
          </Box>

          <Box
            sx={{
              p: 2,
              bgcolor: 'background.default',
              borderRadius: 1,
              border: 1,
              borderColor: 'divider',
            }}
          >

            <TextField
              label="Base Name"
              value={baseName}
              onChange={(e) => setBaseName(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ''))}
              fullWidth
              size="small"
              sx={{ mb: 2 }}
              error={baseName.length > 0 && !baseNameValid}
              helperText={
                baseName.length > 0 && !baseNameValid
                  ? 'Must start with a lowercase letter, only lowercase letters/numbers/underscores allowed'
                  : 'Lowercase letters, numbers, and underscores only'
              }
            />

            <Divider sx={{ my: 2 }} />

            <Typography variant="body2" color="text.secondary" gutterBottom>
              Preview - Variables to be created:
            </Typography>
            <Box component="ul" sx={{ mt: 0.5, mb: 0, pl: 3 }}>
              {variablePreview.map((v) => (
                <Typography component="li" variant="body2" key={v.name}>
                  {v.name} ({v.description})
                </Typography>
              ))}
              {variablePreview.length === 0 && (
                <Typography component="li" variant="body2" color="text.secondary">
                  No framework available to preview variables
                </Typography>
              )}
            </Box>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} variant="outlined">
          Cancel
        </Button>
        {/* data-voice="confirm": Douglas asks before pressing (#1363) — starts the data import */}
        <Button
          onClick={handleConfirm}
          variant="contained"
          color="info"
          disabled={loading || !baseNameValid}
          data-voice="confirm"
        >
          Confirm Import
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default ImportConfirmationModal
